import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Select,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertCircle, AlertTriangle, Clock } from 'react-feather';
import type { GroupWithMembers, GroupInviteStatus } from '../../../services/api/groups';
import { groupsApi } from '../../../services/api/groups';
import type { TimeSlotResponse } from '../../../types';
import { getTimezoneAbbreviation } from '../../../utils/timezone';

interface EditGroupTimeSlotModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  group: GroupWithMembers | null;
  timeSlots: TimeSlotResponse[];
  onUpdated?: () => void;
}

// Format time slot for display
const formatTimeSlotOption = (slot: TimeSlotResponse): string => {
  const dayMap: Record<string, string> = {
    monday: 'Mondays',
    tuesday: 'Tuesdays',
    wednesday: 'Wednesdays',
    thursday: 'Thursdays',
    friday: 'Fridays',
    saturday: 'Saturdays',
    sunday: 'Sundays',
  };
  const day = dayMap[slot.day_of_week.toLowerCase()] || slot.day_of_week;
  const timeParts = slot.time.split(':');
  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;
  const formattedTime = `${hours}:${minutes} ${ampm}`;
  const tz = getTimezoneAbbreviation(slot.timezone);
  return `${day} ${formattedTime} ${tz}`;
};

export function EditGroupTimeSlotModal({
  opened,
  onClose,
  cohortId,
  group,
  timeSlots,
  onUpdated,
}: EditGroupTimeSlotModalProps) {
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<GroupInviteStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Fetch invite status when modal opens
  useEffect(() => {
    if (opened && group) {
      setLoadingStatus(true);
      groupsApi
        .getInviteStatus(cohortId, group.id)
        .then(setInviteStatus)
        .catch(() => setInviteStatus(null))
        .finally(() => setLoadingStatus(false));

      // Pre-select current time slot
      setSelectedSlotId(group.scheduled_time_slot_id ?? null);
    }
  }, [opened, group, cohortId]);

  const handleClose = () => {
    setSelectedSlotId(null);
    setShowConfirm(false);
    setInviteStatus(null);
    onClose();
  };

  const handleChangeClick = () => {
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    if (!group || !selectedSlotId) return;

    const selectedSlot = timeSlots.find((s) => s.id === selectedSlotId);
    if (!selectedSlot) return;

    setUpdating(true);
    try {
      await groupsApi.update(cohortId, group.id, {
        timeSlotIdentifier: selectedSlot.identifier,
      });

      notifications.show({
        title: 'Time Slot Updated',
        message: `${group.name} has been moved to ${formatTimeSlotOption(selectedSlot)}. Future sessions have been rescheduled.`,
        color: 'green',
      });

      onUpdated?.();
      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update time slot',
        color: 'red',
      });
    } finally {
      setUpdating(false);
    }
  };

  const currentSlot = timeSlots.find((s) => s.id === group?.scheduled_time_slot_id);
  const selectedSlot = timeSlots.find((s) => s.id === selectedSlotId);
  const isChanging = selectedSlotId !== group?.scheduled_time_slot_id;
  const hasMixedInviteStatus =
    inviteStatus && inviteStatus.invitedSessions > 0 && inviteStatus.uninvitedSessions > 0;

  // Filter to only show different time slots (exclude current)
  const timeSlotOptions = timeSlots.map((slot) => ({
    value: slot.id,
    label: formatTimeSlotOption(slot),
    disabled: slot.id === group?.scheduled_time_slot_id,
  }));

  return (
    <Modal opened={opened} onClose={handleClose} title="Change Group Time Slot" size="md">
      <Stack gap="md">
        {loadingStatus ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : inviteStatus?.isFullyLocked ? (
          // AC6/AC7: Fully locked state
          <Stack gap="md">
            <Alert icon={<AlertCircle size={16} />} color="red" variant="light">
              <Text size="sm">
                All future sessions for <strong>{group?.name}</strong> have invitations sent.
                The time slot cannot be changed. Use individual session rescheduling instead.
              </Text>
            </Alert>
            <Group justify="flex-end">
              <Button variant="default" onClick={handleClose}>
                Close
              </Button>
            </Group>
          </Stack>
        ) : !showConfirm ? (
          // Step 1: Select new time slot
          <Stack gap="md">
            {group && currentSlot && (
              <Text size="sm">
                <strong>{group.name}</strong> is currently scheduled for{' '}
                <strong>{formatTimeSlotOption(currentSlot)}</strong>. Select a new time slot below.
              </Text>
            )}

            {/* AC9: Mixed invite status warning */}
            {hasMixedInviteStatus && (
              <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
                <Text size="sm">
                  {inviteStatus.invitedSessions} of {inviteStatus.totalFutureSessions} future
                  session(s) already have invitations sent and will <strong>not</strong> be
                  rescheduled. Only {inviteStatus.uninvitedSessions} uninvited session(s) will be
                  updated.
                </Text>
              </Alert>
            )}

            <Select
              label="New Time Slot"
              placeholder="Choose a time slot"
              leftSection={<Clock size={14} />}
              data={timeSlotOptions}
              value={selectedSlotId}
              onChange={setSelectedSlotId}
              searchable
            />

            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleChangeClick} disabled={!isChanging}>
                Change Time Slot
              </Button>
            </Group>
          </Stack>
        ) : (
          // Step 2: Confirmation (AC5)
          <Stack gap="md">
            <Text size="sm" fw={500}>
              Confirm time slot change for {group?.name}:
            </Text>

            <Stack gap={4} ml="md">
              <Text size="sm">
                <strong>From:</strong>{' '}
                {currentSlot ? formatTimeSlotOption(currentSlot) : 'None'}
              </Text>
              <Text size="sm">
                <strong>To:</strong>{' '}
                {selectedSlot ? formatTimeSlotOption(selectedSlot) : '—'}
              </Text>
            </Stack>

            {/* AC5: Number of sessions to be updated */}
            {inviteStatus && (
              <Alert icon={<AlertCircle size={16} />} color="blue" variant="light">
                <Text size="sm">
                  <strong>{inviteStatus.uninvitedSessions}</strong> future session(s) will be
                  rescheduled to the new time slot.
                  {inviteStatus.invitedSessions > 0 && (
                    <>
                      {' '}
                      <strong>{inviteStatus.invitedSessions}</strong> session(s) with invitations
                      already sent will be skipped.
                    </>
                  )}
                </Text>
              </Alert>
            )}

            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => setShowConfirm(false)}>
                Back
              </Button>
              <Button onClick={handleConfirm} loading={updating} color="medleyGreen">
                Confirm Change
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}

export default EditGroupTimeSlotModal;
